import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Share2, Clock, CheckCircle, AlertCircle, Send, Plus, Ban,
  ExternalLink, RefreshCw, Linkedin, Play, Pause,
} from 'lucide-react';
import { useProject } from '../../../contexts/ProjectContext';
import { StatCard } from '../../../components/shared';
import {
  Card, Button,
  Table, TableHead, TableBody, TableRow, TableCell, TableHeaderCell,
  Modal, ModalHeader, ModalBody, ModalFooter,
  Select, Input, Checkbox, Skeleton, EmptyState,
} from '../../../components/ui';
import { StatusPill } from '@archieai/ui';
import {
  contentApi,
  SCHEDULE_STATUS_LABELS,
  SCHEDULE_TYPE_LABELS,
  type ContentSchedule,
  type ContentItem,
  type Author,
  type ScheduleType,
  type ScheduleStatus,
} from '../../../api/content';
import { DashboardPageLayout } from '../../../components/layout';

// =============================================================================
// Schedule Distribution Dialog
// =============================================================================

const SCHEDULE_BADGE_MAP: Record<ScheduleStatus, 'neutral' | 'warning' | 'info' | 'success' | 'error'> = {
  pending: 'warning',
  executing: 'info',
  completed: 'success',
  failed: 'error',
  cancelled: 'neutral',
};

function ScheduleDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { currentProject } = useProject();
  const workspaceId = currentProject?.workspaceId;
  const [items, setItems] = useState<ContentItem[]>([]);
  const [authors, setAuthors] = useState<Author[]>([]);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [scheduleType, setScheduleType] = useState<ScheduleType>('publish');
  const [selectedAuthorIds, setSelectedAuthorIds] = useState<string[]>([]);
  const [scheduledAt, setScheduledAt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !workspaceId) return;
    Promise.all([
      contentApi.listItems(workspaceId),
      contentApi.listAuthors(workspaceId),
    ]).then(([items, authors]) => {
      setItems(items);
      setAuthors(authors);
    });
  }, [open, workspaceId]);

  const handleSubmit = async () => {
    if (!workspaceId || !selectedItemId) return;
    setSubmitting(true);
    setError('');
    try {
      if (scheduleType === 'publish') {
        await contentApi.publishItem(workspaceId, selectedItemId, scheduledAt || undefined);
      } else {
        await contentApi.distributeSocial(
          workspaceId,
          selectedItemId,
          selectedAuthorIds,
          ['linkedin'],
          scheduledAt || undefined,
        );
      }
      onCreated();
      onClose();
    } catch (err) {
      setError((err as Error).message || 'Failed to schedule');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleAuthor = (id: string) => {
    setSelectedAuthorIds(prev =>
      prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
    );
  };

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} size="lg">
      <ModalHeader title="Schedule Distribution" subtitle="Queue content for publishing or social distribution" />
      <ModalBody className="space-y-4">
        <Select
          label="Content Item"
          value={selectedItemId}
          onChange={e => setSelectedItemId(e.target.value)}
        >
          <option value="">Select content...</option>
          {items.map(item => (
            <option key={item.id} value={item.id}>{item.title}</option>
          ))}
        </Select>

        <div>
          <label className="text-sm font-medium text-neutral-700 mb-1.5 block">Distribution Type</label>
          <div className="flex gap-2">
            {(['publish', 'social_distribute'] as ScheduleType[]).map(type => (
              <Button
                key={type}
                variant={scheduleType === type ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setScheduleType(type)}
                className="flex-1"
              >
                {SCHEDULE_TYPE_LABELS[type]}
              </Button>
            ))}
          </div>
        </div>

        {scheduleType === 'social_distribute' && (
          <div>
            <label className="text-sm font-medium text-neutral-700 mb-1.5 block">Target Authors</label>
            {authors.length === 0 ? (
              <p className="text-sm text-neutral-400">No authors available</p>
            ) : (
              <div className="space-y-1.5">
                {authors.map(author => (
                  <Checkbox
                    key={author.id}
                    label={author.name}
                    checked={selectedAuthorIds.includes(author.id)}
                    onChange={() => toggleAuthor(author.id)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        <Input
          type="datetime-local"
          label="Schedule For"
          value={scheduledAt}
          onChange={e => setScheduledAt(e.target.value)}
          helperText="Leave empty to execute immediately"
        />

        {error && (
          <div className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          disabled={!selectedItemId}
          loading={submitting}
        >
          Schedule
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// =============================================================================
// Distribution Page
// =============================================================================

const AUTO_REFRESH_INTERVAL = 15_000; // 15 seconds

const DistributionPage = () => {
  const navigate = useNavigate();
  const { currentProject, buildProjectUrl } = useProject();
  const workspaceId = currentProject?.workspaceId;
  const [queue, setQueue] = useState<ContentSchedule[]>([]);
  const [allSchedules, setAllSchedules] = useState<ContentSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [viewMode, setViewMode] = useState<'active' | 'all'>('active');

  const fetchQueue = useCallback(() => {
    if (!workspaceId) return;
    setLoading(true);
    Promise.all([
      contentApi.getDistributionQueue(workspaceId),
      contentApi.getAllSchedules(workspaceId),
    ])
      .then(([queueData, allData]) => {
        setQueue(queueData);
        setAllSchedules(allData);
      })
      .catch(() => {
        setQueue([]);
        setAllSchedules([]);
      })
      .finally(() => setLoading(false));
  }, [workspaceId]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  // Auto-refresh when active items exist
  useEffect(() => {
    if (!autoRefresh) return;
    const hasActive = queue.some(s => s.status === 'pending' || s.status === 'executing');
    if (!hasActive) return;

    const interval = setInterval(fetchQueue, AUTO_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [autoRefresh, queue, fetchQueue]);

  const handleCancel = async (scheduleId: string) => {
    if (!workspaceId) return;
    try {
      await contentApi.cancelSchedule(workspaceId, scheduleId);
      fetchQueue();
    } catch (err) {
      console.error('Failed to cancel schedule', err);
    }
  };

  const displayedSchedules = viewMode === 'active' ? queue : allSchedules;

  const stats = useMemo(() => {
    const pending = allSchedules.filter(s => s.status === 'pending' || s.status === 'executing').length;
    const completed = allSchedules.filter(s => s.status === 'completed').length;
    const failed = allSchedules.filter(s => s.status === 'failed').length;
    return { pending, completed, failed };
  }, [allSchedules]);

  return (
    <DashboardPageLayout
      title="Channels"
      subtitle="Manage content publishing and social distribution"
      icon={<Share2 size={20} />}
      actions={
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<RefreshCw size={14} className={autoRefresh ? 'animate-spin-slow' : ''} />}
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={autoRefresh ? 'text-blue-500' : 'text-neutral-400'}
          >
            {autoRefresh ? 'Auto' : 'Manual'}
          </Button>
          <Button
            leftIcon={<Plus size={14} />}
            onClick={() => setShowScheduleDialog(true)}
          >
            Schedule Distribution
          </Button>
        </div>
      }
    >
      <div className="space-y-8">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <StatCard icon={<Clock size={20} />} value={stats.pending} label="Queued" color="amber" />
          <StatCard icon={<CheckCircle size={20} />} value={stats.completed} label="Completed" color="emerald" />
          <StatCard icon={<AlertCircle size={20} />} value={stats.failed} label="Failed" color="red" />
        </div>

        {/* View toggle + Queue Table */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Button
              variant={viewMode === 'active' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('active')}
            >
              Active Queue ({queue.length})
            </Button>
            <Button
              variant={viewMode === 'all' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('all')}
            >
              All Schedules ({allSchedules.length})
            </Button>
          </div>

          <Card padding="none">
            {loading ? (
              <div className="p-4 space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="flex items-center gap-4">
                    <Skeleton variant="text" width={192} height={16} />
                    <Skeleton variant="text" width={96} height={16} />
                    <Skeleton variant="text" width={128} height={16} />
                  </div>
                ))}
              </div>
            ) : displayedSchedules.length === 0 ? (
              <EmptyState
                size="lg"
                icon={<Share2 size={40} className="text-neutral-300" />}
                title={viewMode === 'active' ? 'No active distributions' : 'No scheduled distributions yet'}
                description="Click 'Schedule Distribution' to queue content for publishing or social sharing"
              />
            ) : (
              <Table size="sm">
                <TableHead>
                  <TableRow hoverable={false}>
                    <TableHeaderCell className="w-[25%]">Content</TableHeaderCell>
                    <TableHeaderCell>Type</TableHeaderCell>
                    <TableHeaderCell>Scheduled</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell>Authors</TableHeaderCell>
                    <TableHeaderCell>Result</TableHeaderCell>
                    <TableHeaderCell>Actions</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedSchedules.map(schedule => (
                    <TableRow
                      key={schedule.id}
                      onClick={() => navigate(buildProjectUrl(`co/${schedule.content_item_id}`, 'sm'))}
                      className="cursor-pointer"
                    >
                      <TableCell className="font-medium text-neutral-800 truncate">
                        {schedule.content_title || schedule.content_item_id.slice(0, 8)}
                      </TableCell>
                      <TableCell className="text-xs text-neutral-500">
                        {SCHEDULE_TYPE_LABELS[schedule.schedule_type]}
                      </TableCell>
                      <TableCell className="text-xs text-neutral-500">
                        {new Date(schedule.scheduled_at).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                        })}
                      </TableCell>
                      <TableCell>
                        <StatusPill
                          variant={SCHEDULE_BADGE_MAP[schedule.status]}
                          label={SCHEDULE_STATUS_LABELS[schedule.status]}
                        />
                      </TableCell>
                      <TableCell className="text-xs text-neutral-500">
                        {schedule.target_author_ids.length > 0
                          ? `${schedule.target_author_ids.length} author${schedule.target_author_ids.length > 1 ? 's' : ''}`
                          : '\u2014'}
                      </TableCell>
                      <TableCell>
                        {schedule.status === 'completed' && schedule.executed_at && (
                          <span className="text-[11px] text-emerald-600 flex items-center gap-1">
                            <CheckCircle size={10} />
                            {new Date(schedule.executed_at).toLocaleDateString('en-US', {
                              month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                            })}
                          </span>
                        )}
                        {schedule.status === 'executing' && (
                          <span className="text-[11px] text-blue-500 flex items-center gap-1">
                            <RefreshCw size={10} className="animate-spin" />
                            In progress
                          </span>
                        )}
                        {schedule.status === 'failed' && schedule.error_message && (
                          <span
                            className="text-[10px] text-red-500 truncate max-w-[200px] inline-block"
                            title={schedule.error_message}
                          >
                            {schedule.error_message}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {schedule.status === 'pending' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            leftIcon={<Ban size={12} />}
                            onClick={e => {
                              e.stopPropagation();
                              handleCancel(schedule.id);
                            }}
                            className="text-neutral-400 hover:text-red-500"
                          >
                            Cancel
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </div>

        {/* LinkedIn Integration Status */}
        <Card padding="lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                <Linkedin className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="text-sm font-medium text-neutral-800">LinkedIn Distribution</div>
                <div className="text-xs text-neutral-400">
                  Automated posting via Voyager API. Configure author credentials in author settings.
                </div>
              </div>
            </div>
            <StatusPill variant="success" label="Connected" showDot />
          </div>
        </Card>

        {/* Schedule Dialog */}
        <ScheduleDialog
          open={showScheduleDialog}
          onClose={() => setShowScheduleDialog(false)}
          onCreated={fetchQueue}
        />
      </div>
    </DashboardPageLayout>
  );
};

export default DistributionPage;
